import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-md hover:bg-primary-light hover:-translate-y-0.5 active:translate-y-0",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: 
          "hover:bg-accent/10 hover:text-accent",
        link: 
          "text-primary underline-offset-4 hover:underline",
        // Custom Blue Lion Variants
        gold:
          "bg-gradient-to-r from-gold to-gold-light text-gold-foreground shadow-gold hover:-translate-y-0.5 hover:shadow-[0_6px_20px_0_hsl(43_87%_50%/0.45)] active:translate-y-0",
        accent:
          "bg-gradient-to-r from-accent to-accent-glow text-accent-foreground shadow-accent hover:-translate-y-0.5 hover:shadow-[0_6px_20px_0_hsl(210_100%_50%/0.35)] active:translate-y-0",
        navy:
          "bg-primary text-primary-foreground hover:bg-primary-light hover:-translate-y-0.5",
        "outline-gold":
          "border-2 border-gold bg-transparent text-gold hover:bg-gold/10",
        "outline-accent":
          "border-2 border-accent bg-transparent text-accent hover:bg-accent/10",
        "ghost-light":
          "text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/10",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-md px-4 text-xs",
        lg: "h-12 rounded-xl px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
